import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Tabs, Tab, Alert, Spinner, ListGroup, Badge, InputGroup, Card } from 'react-bootstrap';
import { FaComments, FaPaperPlane, FaMagic, FaPaintBrush } from 'react-icons/fa';
import axios from 'axios';
import DrawingCanvas from './DrawingCanvas';
import { API_URL } from '../config';

function ConfigEditor({ show, onHide, type = 'supervisor', agentName = null }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [content, setContent] = useState('');
  const [originalContent, setOriginalContent] = useState('');
  const [activeTab, setActiveTab] = useState('prompt');
  
  // Sample files state
  const [samples, setSamples] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [showDrawingCanvas, setShowDrawingCanvas] = useState(false);
  
  // AI Chat state
  const [chatMessage, setChatMessage] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [isChatting, setIsChatting] = useState(false);
  const [chatResult, setChatResult] = useState(null);
  
  // Refine prompt state
  const [isRefining, setIsRefining] = useState(false);
  const [refinedPrompt, setRefinedPrompt] = useState(null);
  const [showRefineResult, setShowRefineResult] = useState(false);
  
  // Multi-agent editor state
  const [useMultiAgent, setUseMultiAgent] = useState(true);
  const [agentPipeline, setAgentPipeline] = useState([
    { name: 'context', label: 'Context Analysis', status: 'pending' },
    { name: 'code_edit', label: 'Code Editor', status: 'pending' },
    { name: 'dependency_fix', label: 'Dependencies', status: 'pending' },
    { name: 'style_fix', label: 'Style Check', status: 'pending' },
    { name: 'validate', label: 'Validation', status: 'pending' },
  ]);
  const [editResult, setEditResult] = useState(null);

  const backendUrl = API_URL;

  useEffect(() => {
    if (show) {
      // Load content when on prompt or code tabs
      if (activeTab === 'prompt' || activeTab === 'code') {
        loadContent();
      }
      if (activeTab === 'samples' && agentName) {
        loadSamples();
      }
    }
  }, [show, type, agentName, activeTab]);

  const loadContent = async () => {
    // Don't load if modal is not shown or we're on samples/ai_chat tab
    if (!show || activeTab === 'samples' || activeTab === 'ai_chat') return;
    
    setLoading(true);
    setError(null);
    
    try {
      let url;
      if (type === 'supervisor') {
        url = `${backendUrl}/api/manifest/supervisor-prompt`;
      } else if (type === 'agent' && agentName) {
        if (activeTab === 'prompt') {
          url = `${backendUrl}/api/manifest/agent-prompt/${agentName}`;
        } else if (activeTab === 'code') {
          url = `${backendUrl}/api/manifest/agent-code/${agentName}`;
        }
      } else {
        // Invalid configuration
        setError('Invalid editor configuration');
        setLoading(false);
        return;
      }

      const response = await axios.get(url);
      const data = response.data;
      
      if (data.success) {
        const loadedContent = data.prompt || data.code || '';
        setContent(loadedContent);
        setOriginalContent(loadedContent);
      } else {
        setError(String(data.error) || 'Failed to load configuration');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Failed to load configuration';
      setError(String(errorMsg));
    } finally {
      setLoading(false);
    }
  };

  const loadSamples = async () => {
    if (!agentName) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${backendUrl}/api/agent-samples/${agentName}`);
      setSamples(response.data || []);
    } catch (err) {
      console.error('Failed to load samples:', err);
      setSamples([]);
    } finally {
      setLoading(false);
    }
  };

  const uploadSample = async (file, description) => {
    setUploadingFile(true);
    setError(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (description) {
        formData.append('description', description);
      }
      
      await axios.post(`${backendUrl}/api/agent-samples/${agentName}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setSuccess(`✅ Uploaded ${file.name}`);
      setTimeout(() => setSuccess(null), 2000);
      loadSamples();
    } catch (err) {
      setError(`Failed to upload: ${err.response?.data?.error || err.message}`);
    } finally {
      setUploadingFile(false);
    }
  };

  const deleteSample = async (sampleId, filename) => {
    if (!window.confirm(`Delete ${filename}?`)) return;
    
    try {
      await axios.delete(`${backendUrl}/api/agent-samples/${sampleId}`);
      setSuccess(`✅ Deleted ${filename}`);
      setTimeout(() => setSuccess(null), 2000);
      loadSamples();
    } catch (err) {
      setError(`Failed to delete: ${err.response?.data?.error || err.message}`);
    }
  };

  // Refine prompt with AI
  const handleRefinePrompt = async () => {
    if (!content.trim()) {
      setError('No prompt content to refine');
      return;
    }
    
    setIsRefining(true);
    setError(null);
    
    try {
      const response = await axios.post(`${backendUrl}/api/smart-agent/refine-prompt`, {
        purpose: content,
        mode: 'edit'  // Use edit mode to preserve prompt structure and detail
      });
      
      if (response.data.success) {
        setRefinedPrompt(response.data);
        setShowRefineResult(true);
      } else {
        setError(response.data.error || 'Failed to refine prompt');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to refine prompt');
    } finally {
      setIsRefining(false);
    }
  };

  // Accept the refined prompt
  const handleAcceptRefinedPrompt = () => {
    // Edit mode returns refined_prompt; create mode returns refined_purpose
    const refined = refinedPrompt?.refined_prompt || refinedPrompt?.refined_purpose;
    if (refined) {
      setContent(refined);
    }
    setShowRefineResult(false);
  };

  const handleChat = async () => {
    if (!chatMessage.trim() || !content) return;
    
    setIsChatting(true);
    setError(null);
    setEditResult(null);
    
    // Reset pipeline status
    setAgentPipeline(prev => prev.map(a => ({ ...a, status: 'pending' })));
    
    try {
      if (useMultiAgent) {
        // Use the streaming multi-agent editor to avoid 502 timeouts
        const response = await fetch(`${backendUrl}/api/smart-agent/edit-v2-stream`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('jwt_token') || ''}` },
          body: JSON.stringify({
            message: chatMessage,
            code: content,
            agent_name: agentName
          })
        });

        if (!response.ok) {
          throw new Error(`Server error: ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let finalCode = null;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith(': keepalive') || !line.trim()) continue;
            if (!line.startsWith('data: ')) continue;

            try {
              const event = JSON.parse(line.slice(6));

              if (event.type === 'agent_update') {
                // Update pipeline status for this agent
                const agentNameKey = event.agent;
                setAgentPipeline(prev => prev.map(a => ({
                  ...a,
                  status: a.name === agentNameKey ? 'complete' :
                          (a.status === 'complete' ? 'complete' : a.status)
                })));

                // If this agent produced updated code, capture it
                if (event.data?.code) {
                  finalCode = event.data.code;
                }
                if (event.data?.edits) {
                  setEditResult(prev => ({
                    ...prev,
                    edits: [...(prev?.edits || []), ...event.data.edits],
                    syntax_valid: event.data.syntax_valid ?? prev?.syntax_valid,
                    syntax_error: event.data.syntax_error ?? prev?.syntax_error,
                    save_result: event.data.save_result ?? prev?.save_result,
                    dependency_result: event.data.dependency_result ?? prev?.dependency_result,
                  }));
                }
              } else if (event.type === 'complete') {
                // Mark all pipeline stages as complete
                setAgentPipeline(prev => prev.map(a => ({ ...a, status: 'complete' })));
                // Capture final code from the complete event
                if (event.code) {
                  finalCode = event.code;
                }
                if (event.save_result) {
                  setEditResult(prev => ({
                    ...prev,
                    save_result: event.save_result,
                  }));
                }
              } else if (event.type === 'error') {
                setError(event.error || 'Multi-agent edit failed');
              }
            } catch (parseErr) {
              // Skip malformed lines
            }
          }
        }

        // Update content with final code if we got any
        if (finalCode) {
          setContent(finalCode);
        }
        setChatMessage('');
      } else {
        // One-shot edit-chat endpoint (Claude primary, o3 fallback)
        const response = await axios.post(`${backendUrl}/api/smart-agent/edit-chat`, {
          message: chatMessage,
          code: content,
          agent_name: agentName,
          history: chatHistory
        });
        
        if (response.data.success) {
          setChatHistory(response.data.history);
          
          if (response.data.code) {
            setContent(response.data.code);
            
            setChatResult({
              syntax_valid: response.data.syntax_valid,
              syntax_error: response.data.syntax_error,
              explanation: response.data.explanation,
              pattern_issues: response.data.pattern_issues || [],
              test_result: response.data.test_result,
              test_passed: response.data.test_passed,
              has_issues: response.data.has_issues,
              quality: response.data.quality,
              dependencies: response.data.dependencies || []
            });
          }
          
          setChatMessage('');
        } else {
          setError(response.data.error || 'Chat request failed');
        }
      }
    } catch (err) {
      console.error('Chat error:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Failed to chat with AI';
      setError(`AI Chat Error: ${errorMsg}`);
    } finally {
      setIsChatting(false);
    }
  };

  const saveContent = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      let url;
      let payload;
      
      // Configure axios with increased limits and timeout
      const axiosConfig = {
        timeout: 60000, // 60 second timeout
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
        headers: {
          'Content-Type': 'application/json'
        }
      };
      
      if (type === 'supervisor') {
        url = `${backendUrl}/api/manifest/supervisor-prompt`;
        payload = { prompt: content };
      } else if (type === 'agent' && agentName) {
        if (activeTab === 'prompt') {
          url = `${backendUrl}/api/manifest/agent-prompt/${agentName}`;
          payload = { prompt: content };
        } else {
          // For 'code' or 'ai_chat' tabs, save as code
          url = `${backendUrl}/api/manifest/agent-code/${agentName}`;
          payload = { code: content };
        }
      }

      const saveResponse = await axios.post(url, payload, axiosConfig);

      let successMsg = '✅ Saved! (Backup created)';
      
      // Check if dependencies were installed
      if (saveResponse.data?.dependencies_detected?.length > 0) {
        const deps = saveResponse.data.dependencies_detected;
        successMsg = `✅ Saved! Installed ${deps.length} dependencies: ${deps.join(', ')}`;
      }
      
      setSuccess(successMsg);
      setOriginalContent(content);
      
      // If saving agent code, trigger supervisor reload
      if (type === 'agent' && (activeTab === 'code' || activeTab === 'ai_chat')) {
        try {
          await axios.post(`${backendUrl}/api/agents/reload`);
          setSuccess(successMsg + ' & Supervisor Reloaded!');
        } catch (reloadErr) {
          console.warn('Could not reload supervisor:', reloadErr);
          setSuccess(successMsg + ' (Reload supervisor manually to use updated agent)');
        }
      }
      
      // Auto-hide success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Save error:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Failed to save';
      setError(String(errorMsg));
    } finally {
      setLoading(false);
    }
  };

  const hasChanges = content !== originalContent;

  const getTitle = () => {
    if (type === 'supervisor') {
      return '⚙️ Supervisor Configuration';
    } else if (type === 'agent' && agentName) {
      return `⚙️ Configure ${agentName}`;
    }
    return '⚙️ Configuration';
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>{getTitle()}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {type === 'agent' && agentName && (
          <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-3">
            <Tab eventKey="prompt" title="📝 Prompt">
              {/* Prompt editor */}
            </Tab>
            <Tab eventKey="code" title="💻 Code">
              {/* Code editor */}
            </Tab>
            <Tab eventKey="ai_chat" title="💬 AI Chat">
              {/* AI Chat for iterative fixes */}
            </Tab>
            <Tab eventKey="samples" title="📦 Samples">
              {/* Sample files for RAG/few-shot */}
            </Tab>
          </Tabs>
        )}

        {loading && activeTab !== 'ai_chat' ? (
          <div className="text-center py-5">
            <Spinner animation="border" />
            <p className="mt-2">Loading...</p>
          </div>
        ) : activeTab === 'ai_chat' ? (
          // AI Chat tab content
          <div>
            <Alert variant="info">
              <FaComments className="me-2" />
              <strong>AI Chat Assistant</strong> - Get help fixing or improving your agent code iteratively
            </Alert>
            
            {!content && (
              <Alert variant="warning" className="d-flex align-items-center justify-content-between">
                <div>
                  <strong>⚠️ No code loaded.</strong> Switch to the <strong>Code</strong> tab first to load the agent code, then return here to chat.
                </div>
                <Button 
                  variant="warning" 
                  size="sm"
                  onClick={() => setActiveTab('code')}
                >
                  Go to Code Tab
                </Button>
              </Alert>
            )}
            
            {chatResult && (
              <Alert variant={!chatResult.has_issues ? 'success' : 'warning'} className="mb-3">
                <strong>🔍 Code Validation Results:</strong>
                
                {/* Quality badges */}
                <div className="mt-2 d-flex gap-2 flex-wrap">
                  <Badge bg={chatResult.syntax_valid ? 'success' : 'danger'}>
                    {chatResult.syntax_valid ? '✅' : '❌'} Syntax
                  </Badge>
                  
                  {chatResult.test_passed !== undefined && (
                    <Badge bg={chatResult.test_passed ? 'success' : 'danger'}>
                      {chatResult.test_passed ? '✅' : '❌'} Agent Test
                    </Badge>
                  )}
                  
                  {chatResult.pattern_issues && chatResult.pattern_issues.length > 0 && (
                    <Badge bg="warning">
                      ⚠️ {chatResult.pattern_issues.length} Placeholder{chatResult.pattern_issues.length > 1 ? 's' : ''}
                    </Badge>
                  )}
                  
                  {!chatResult.has_issues && (
                    <Badge bg="success">🎯 Production Ready</Badge>
                  )}
                  
                  {chatResult.dependencies && chatResult.dependencies.length > 0 && (
                    <Badge bg="info">
                      📦 {chatResult.dependencies.length} Dependencies
                    </Badge>
                  )}
                </div>
                
                {/* Dependencies detected */}
                {chatResult.dependencies && chatResult.dependencies.length > 0 && (
                  <Alert variant="info" className="mt-2 mb-0 small">
                    <strong>📦 Dependencies Detected:</strong> {chatResult.dependencies.join(', ')}
                    <div className="mt-1 text-muted">
                      <small>Will auto-install when you save</small>
                    </div>
                  </Alert>
                )}
                
                {/* Syntax error details */}
                {chatResult.syntax_error && (
                  <Alert variant="danger" className="mt-2 mb-0 small">
                    <strong>Syntax Error:</strong> {chatResult.syntax_error}
                  </Alert>
                )}
                
                {/* Pattern issues */}
                {chatResult.pattern_issues && chatResult.pattern_issues.length > 0 && (
                  <Alert variant="warning" className="mt-2 mb-0 small">
                    <strong>⚠️ Placeholders Detected:</strong>
                    {chatResult.pattern_issues.map((issue, idx) => (
                      <div key={idx} className="mt-1">
                        <Badge bg="warning">Line {issue.line}</Badge> {issue.pattern}
                        <div><code>{issue.code}</code></div>
                      </div>
                    ))}
                  </Alert>
                )}
                
                {/* Test failure details */}
                {chatResult.test_result && !chatResult.test_passed && (
                  <Alert variant="danger" className="mt-2 mb-0 small">
                    <strong>❌ Agent Test Failed:</strong>
                    <div className="mt-1">{chatResult.test_result.error}</div>
                    {chatResult.test_result.traceback && (
                      <details className="mt-1">
                        <summary style={{cursor: 'pointer'}}>Show traceback</summary>
                        <pre className="mt-1 small">{chatResult.test_result.traceback}</pre>
                      </details>
                    )}
                  </Alert>
                )}
                
                {/* AI explanation */}
                {!chatResult.has_issues && (
                  <div className="mt-2 p-2 bg-light border-start border-success border-3 small" style={{whiteSpace: 'pre-wrap'}}>
                    {chatResult.explanation}
                  </div>
                )}
              </Alert>
            )}
            
            {/* Multi-Agent Editor Toggle and Pipeline */}
            <Card className="mb-3 border-info">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <span>🤖 Edit Mode</span>
                <Form.Check 
                  type="switch"
                  id="multi-agent-toggle"
                  label={useMultiAgent ? "Multi-Agent Editor (v2)" : "One-Shot Editor"}
                  checked={useMultiAgent}
                  onChange={(e) => setUseMultiAgent(e.target.checked)}
                />
              </Card.Header>
              {useMultiAgent && (
                <Card.Body>
                  <div className="d-flex gap-2 flex-wrap mb-2">
                    {agentPipeline.map((agent, idx) => (
                      <Badge 
                        key={agent.name}
                        bg={
                          agent.status === 'complete' ? 'success' :
                          agent.status === 'running' ? 'primary' :
                          agent.status === 'error' ? 'danger' : 'secondary'
                        }
                        style={{ fontSize: '0.8rem', padding: '0.4em 0.8em' }}
                      >
                        {agent.status === 'complete' ? '✅' : 
                         agent.status === 'running' ? '⏳' :
                         agent.status === 'error' ? '❌' : '○'} {agent.label}
                      </Badge>
                    ))}
                  </div>
                  <small className="text-muted">
                    Uses specialized agents for context analysis, targeted edits, dependency management, and style checking.
                  </small>
                </Card.Body>
              )}
            </Card>
            
            {/* Multi-Agent Edit Result */}
            {editResult && useMultiAgent && (
              <Alert variant={editResult.syntax_valid ? 'success' : 'warning'} className="mb-3">
                <strong>🔧 Multi-Agent Edit Results:</strong>
                
                <div className="mt-2 d-flex gap-2 flex-wrap">
                  <Badge bg={editResult.syntax_valid ? 'success' : 'danger'}>
                    {editResult.syntax_valid ? '✅' : '❌'} Syntax Valid
                  </Badge>
                  
                  {editResult.edits && editResult.edits.length > 0 && (
                    <Badge bg="info">
                      📝 {editResult.edits.length} Edit{editResult.edits.length > 1 ? 's' : ''} Made
                    </Badge>
                  )}
                  
                  {editResult.save_result?.success && (
                    <Badge bg="success">💾 Auto-Saved</Badge>
                  )}
                  
                  {editResult.dependency_result?.packages_installed?.length > 0 && (
                    <Badge bg="info">
                      📦 Installed: {editResult.dependency_result.packages_installed.join(', ')}
                    </Badge>
                  )}
                </div>
                
                {/* Show edits made */}
                {editResult.edits && editResult.edits.length > 0 && (
                  <details className="mt-2">
                    <summary style={{cursor: 'pointer'}}>View {editResult.edits.length} edit(s)</summary>
                    <div className="mt-1 small">
                      {editResult.edits.map((edit, idx) => (
                        <div key={idx} className="mb-2 p-2 bg-light rounded">
                          <div className="text-danger"><del>{edit.old?.substring(0, 100)}{edit.old?.length > 100 ? '...' : ''}</del></div>
                          <div className="text-success">{edit.new?.substring(0, 100)}{edit.new?.length > 100 ? '...' : ''}</div>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
                
                {editResult.syntax_error && (
                  <Alert variant="danger" className="mt-2 mb-0 small">
                    <strong>Syntax Error:</strong> {editResult.syntax_error}
                  </Alert>
                )}
              </Alert>
            )}

            {/* Chat History */}
            {chatHistory.length > 0 && !useMultiAgent && (
              <Card className="mb-3">
                <Card.Header>Chat History</Card.Header>
                <Card.Body style={{maxHeight: '400px', overflowY: 'auto'}}>
                  {chatHistory.map((msg, idx) => (
                    <div key={idx} className={`mb-3 ${msg.role === 'user' ? 'text-end' : ''}`}>
                      <Badge bg={msg.role === 'user' ? 'primary' : 'secondary'} className="mb-1">
                        {msg.role === 'user' ? 'You' : 'AI'}
                      </Badge>
                      <div className={`p-2 rounded ${msg.role === 'user' ? 'bg-light' : 'bg-white border'}`}>
                        <small style={{whiteSpace: 'pre-wrap'}}>{msg.content}</small>
                      </div>
                    </div>
                  ))}
                </Card.Body>
              </Card>
            )}
            
            {/* Chat Input */}
            <Form.Group>
              <Form.Label>Send a Message</Form.Label>
              <InputGroup>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Describe what you want to fix or improve... e.g., 'Add error handling for file not found'"
                  value={chatMessage}
                  onChange={(e) => setChatMessage(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleChat();
                    }
                  }}
                  disabled={isChatting || !content}
                />
                <Button
                  variant="primary"
                  onClick={handleChat}
                  disabled={isChatting || !chatMessage.trim() || !content}
                >
                  {isChatting ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <>
                      <FaPaperPlane className="me-1" />
                      Send
                    </>
                  )}
                </Button>
              </InputGroup>
              <Form.Text className="text-muted">
                Press Shift+Enter for new line, Enter to send. The AI will analyze your code and suggest improvements.
              </Form.Text>
            </Form.Group>
            
            {content && chatHistory.length === 0 && (
              <Card className="mt-3 border-info">
                <Card.Body>
                  <h6>💡 Tips for using AI Chat:</h6>
                  <ul className="mb-0 small">
                    <li>Ask to fix specific errors or add features</li>
                    <li>Request better error handling or edge case coverage</li>
                    <li>Ask for code optimization or performance improvements</li>
                    <li>Request documentation or type hints</li>
                  </ul>
                  <div className="mt-2 small text-muted">
                    <strong>Example prompts:</strong><br/>
                    "Add validation for empty input"<br/>
                    "Fix the file path handling on Windows"<br/>
                    "Add logging for debugging"
                  </div>
                </Card.Body>
              </Card>
            )}
          </div>
        ) : activeTab === 'samples' ? (
          // Samples tab content
          <div>
            <h6>Reference Samples (RAG/Few-Shot Learning)</h6>
            <p className="text-muted small">
              Upload or draw example files for this agent to reference. These persist with the agent.
            </p>
            
            {/* Upload + Draw */}
            <div className="mb-4 p-3 bg-light rounded d-flex gap-2 align-items-center flex-wrap">
              <Form.Control
                type="file"
                onChange={(e) => {
                  if (e.target.files[0]) {
                    const desc = prompt('Description (optional):');
                    uploadSample(e.target.files[0], desc);
                    e.target.value = '';
                  }
                }}
                disabled={uploadingFile}
                style={{ flex: 1, minWidth: 200 }}
              />
              <Button variant="outline-secondary" size="sm"
                onClick={() => setShowDrawingCanvas(true)} disabled={uploadingFile}>
                <FaPaintBrush className="me-1" /> Draw
              </Button>
              {uploadingFile && <Spinner animation="border" size="sm" />}
            </div>
            
            {/* Samples grid with thumbnails */}
            {samples.length === 0 ? (
              <Alert variant="info">
                No sample files uploaded yet.
              </Alert>
            ) : (
              <div className="d-flex flex-wrap gap-2">
                {samples.map(sample => {
                  const isVisual = ['image', 'svg'].includes(sample.file_type);
                  const thumbUrl = isVisual ? `${backendUrl}/api/agent-samples/file/${agentName}/${sample.filename}` : null;
                  return (
                    <div key={sample.id} className="border rounded p-2 bg-white d-flex align-items-center gap-2" style={{ maxWidth: 260 }}>
                      {thumbUrl ? (
                        <img src={thumbUrl} alt={sample.filename} style={{ width: 48, height: 48, objectFit: 'contain', borderRadius: 4 }} />
                      ) : (
                        <div className="d-flex align-items-center justify-content-center" style={{ width: 48, height: 48, background: '#eee', borderRadius: 4, fontSize: '0.7rem' }}>
                          {sample.file_type}
                        </div>
                      )}
                      <div className="flex-grow-1" style={{ minWidth: 0 }}>
                        <div className="fw-bold small text-truncate">{sample.filename}</div>
                        {sample.description && <div className="text-muted" style={{ fontSize: '0.7rem' }}>{sample.description}</div>}
                      </div>
                      <Button variant="outline-danger" size="sm" onClick={() => deleteSample(sample.id, sample.filename)}>
                        ×
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}

            <DrawingCanvas
              show={showDrawingCanvas}
              onHide={() => setShowDrawingCanvas(false)}
              onSave={(file) => {
                uploadSample(file, 'Drawn reference sample');
              }}
            />
          </div>
        ) : (
          // Prompt/Code editor
          <Form.Group>
            <div className="d-flex justify-content-between align-items-center mb-1">
              <Form.Label className="mb-0">
                {activeTab === 'code' ? 'Python Code' : 'Prompt Text'}
                {hasChanges && <span className="text-warning ms-2">● Unsaved changes</span>}
              </Form.Label>
              {activeTab === 'prompt' && type === 'agent' && (
                <Button
                  variant="outline-info"
                  size="sm"
                  onClick={handleRefinePrompt}
                  disabled={isRefining || !content.trim()}
                >
                  {isRefining ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-1" />
                      Refining...
                    </>
                  ) : (
                    <>
                      <FaMagic className="me-1" />
                      Refine with AI
                    </>
                  )}
                </Button>
              )}
            </div>
            
            {/* Refined prompt result */}
            {showRefineResult && refinedPrompt && activeTab === 'prompt' && (
              <Alert variant="info" className="mb-2" dismissible onClose={() => setShowRefineResult(false)}>
                <Alert.Heading className="h6">
                  <FaMagic className="me-2" />
                  AI-Refined Prompt
                </Alert.Heading>
                <div className="p-2 bg-white rounded border mb-2" style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem', maxHeight: '400px', overflowY: 'auto' }}>
                  {refinedPrompt.refined_prompt || refinedPrompt.refined_purpose}
                </div>
                {refinedPrompt.notes && (
                  <p className="small text-muted mb-2">
                    <strong>Notes:</strong> {refinedPrompt.notes}
                  </p>
                )}
                <div className="d-flex gap-2">
                  <Button variant="success" size="sm" onClick={handleAcceptRefinedPrompt}>
                    ✅ Use Refined Prompt
                  </Button>
                  <Button variant="outline-secondary" size="sm" onClick={() => setShowRefineResult(false)}>
                    Keep Original
                  </Button>
                </div>
              </Alert>
            )}
            
            <Form.Control
              as="textarea"
              rows={20}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              style={{
                fontFamily: activeTab === 'code' ? 'monospace' : 'inherit',
                fontSize: activeTab === 'code' ? '13px' : '14px'
              }}
            />
            <Form.Text className="text-muted">
              {activeTab === 'code' 
                ? 'Edit agent implementation. A backup will be created before saving.'
                : 'Edit instructions that guide agent behavior.'}
            </Form.Text>
          </Form.Group>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={loading}>
          {activeTab === 'samples' || activeTab === 'ai_chat' ? 'Close' : 'Cancel'}
        </Button>
        
        {/* Save button for Code and Prompt tabs */}
        {activeTab !== 'samples' && activeTab !== 'ai_chat' && (
          <Button 
            variant="primary" 
            onClick={saveContent} 
            disabled={loading || !hasChanges}
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              '💾 Save'
            )}
          </Button>
        )}
        
        {/* Save button for AI Chat tab when changes exist */}
        {activeTab === 'ai_chat' && hasChanges && (
          <>
            <Alert variant="info" className="mb-0 me-auto">
              <small>
                💡 AI has updated your code. You can save directly or review in Code tab first.
              </small>
            </Alert>
            <Button 
              variant="success" 
              onClick={saveContent} 
              disabled={loading}
            >
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Saving...
                </>
              ) : (
                '💾 Save AI Changes'
              )}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
}

export default ConfigEditor;
